import { initializeApp, type FirebaseApp } from 'firebase/app';
import {
  initializeAuth, onAuthStateChanged as firebaseOnAuthStateChanged, indexedDBLocalPersistence,
  type Auth,
} from 'firebase/auth';
import { getAnalytics, type Analytics } from 'firebase/analytics';
import { SagaHelpers } from '@sda/sdk/helpers/firestore.helpers/saga.helpers';
import { FirestoreInstanceTypes } from '@sda/enums';
import { SDKOptions } from '@sda/sdk/helpers/sdk.options';
import { BaseSDK } from '@sda/sdk';
import * as methods from './methods';
import type { IntercomMethod } from '@client/intercom';
import type { FirebaseCallbackOptions } from './interfaces';

declare global {
  interface Window {
    Analytics: Analytics;
    Firebase: Firebase;
    __PRELOADED_STATE__: any;
    google: typeof google;
    HWWidgets: any;

    Intercom(callback: IntercomMethod, metadata?: unknown): void;
    Intercom(callback: IntercomMethod, event: string, metadata?: unknown): void;
  }
}

export class Firebase {
  app: FirebaseApp;

  analytics: Analytics;

  auth: Auth;

  helpers: SagaHelpers;

  SDK: BaseSDK;

  constructor(options: FirebaseCallbackOptions) {
    const { onAuthStateChanged = null } = options;

    if (!window.Firebase) {
      this.app = initializeApp({
        apiKey: process.env.FIREBASE_CONFIG_API_KEY,
        authDomain: process.env.FIREBASE_CONFIG_AUTH_DOMAIN,
        databaseURL: process.env.FIREBASE_CONFIG_DATABASE_URL,
        projectId: process.env.FIREBASE_CONFIG_PROJECT_ID,
        storageBucket: process.env.FIREBASE_CONFIG_STORAGE_BUCKET,
        messagingSenderId: process.env.FIREBASE_CONFIG_MESSAGING_SENDER_ID,
        appId: process.env.FIREBASE_CONFIG_WEB_APP_ID,
      });

      // Shortcuts to Firebase SDK features.
      this.auth = initializeAuth(this.app, {
        persistence: [indexedDBLocalPersistence],
      });
    } else {
      this.app = window.Firebase.app;
      this.auth = window.Firebase.auth;
    }

    firebaseOnAuthStateChanged(this.auth, onAuthStateChanged);

    this.analytics = getAnalytics(this.app);
    window.Analytics = this.analytics;

    // Ideally, *this* is what we actually use to interact with the backend.
    const client = {
      methods,
      instanceType: FirestoreInstanceTypes.INSTANCE_TYPE_CLIENT,
      app: this.app,
    };

    SDKOptions.BaseFirestoreHelpers = SagaHelpers;

    this.helpers = new SagaHelpers(client, null);
    this.SDK = new BaseSDK(client);
  }
}
